/**
 * UpdatesPage — CRUD cho bảng lookup_update (Sprint 5).
 * Type Update: update_id (PK), update_date, description, is_status.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import DateTextField from '@/components/DateTextField'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListUpdatesQuery,
  useCreateUpdateMutation,
  useUpdateUpdateMutation,
  useDeleteUpdateMutation,
} from '@/store/api/refsCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { Update } from '@/types/api'

interface UpdFormValues {
  update_date: string
  description: string
}

const EMPTY: UpdFormValues = { update_date: '', description: '' }

export function UpdatesPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<Update | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Update | null>(null)

  const { data, isLoading, error, isFetching } = useListUpdatesQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createUpdate, { isLoading: creating }] = useCreateUpdateMutation()
  const [updateUpdate, { isLoading: updating }] = useUpdateUpdateMutation()
  const [deleteUpdate, { isLoading: deleting }] = useDeleteUpdateMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset, setValue, watch } = useForm<UpdFormValues>({ defaultValues: EMPTY })
  const updateDate = watch('update_date')

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        update_date: editing.update_date ?? '',
        description: editing.description ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.update_id ?? Math.random(), ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'update_date', headerName: 'Ngày', width: 140 },
      { field: 'description', headerName: 'Mô tả', flex: 1, minWidth: 300 },
      {
        field: '_actions',
        headerName: 'Thao tác',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Sửa">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as Update) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Xoá">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as Update) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Update = {
      update_date: values.update_date || null,
      description: values.description || null,
    }
    try {
      if (isEdit && editing && editing.update_id != null) {
        await updateUpdate({ id: editing.update_id, body: payload }).unwrap()
        snack.success('Đã cập nhật update')
      } else {
        await createUpdate(payload).unwrap()
        snack.success('Đã tạo update')
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Cập nhật thất bại' : 'Tạo thất bại'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete || confirmDelete.update_id == null) return
    try {
      await deleteUpdate(confirmDelete.update_id).unwrap()
      snack.success('Đã xoá update')
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Xoá thất bại'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Updates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          Thêm Update
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Tìm kiếm"
          placeholder="Mô tả..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as Update)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Sửa Update' : 'Thêm Update'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <input type="hidden" {...register('update_date')} />
          <DateTextField
            label="Ngày"
            size="small"
            fullWidth
            value={updateDate}
            onChange={(value) => setValue('update_date', value, { shouldDirty: true, shouldTouch: true })}
          />
          <TextField label="Mô tả" size="small" fullWidth multiline rows={4} {...register('description')} />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Xoá Update"
        message="Xác nhận xoá update này?"
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default UpdatesPage
